import { State, process } from '@progress/kendo-data-query';
import { formatNumber } from '@progress/kendo-intl';
import {
  GridDataStateChangeEvent,
  GridDetailRowProps,
  GridPagerSettings,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { TabStrip, TabStripSelectEventArguments, TabStripTab } from '@progress/kendo-react-layout';
import { isEmpty, isNull, orderBy, sumBy } from 'lodash';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  useHoldAmountColumns,
  useHoldAmountDetailColumns,
} from 'src/assets/static/view_checkbook/holdAmountColumns';
import GridWrapper from 'src/components/GridWrapper';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import useCommittedAmountApi, {
  ICommittedAmountTransaction,
} from 'src/services/useCommittedAmountApi';
import { GLCodesBudgetResponseObject } from 'src/services/useGlCodesBudgetApi';
import { round } from 'src/utils';
import exportToExcel from 'src/utils/exportToExcel';
import { fixDateSort } from './CheckbookActualizedAmountTable';
import CheckbookBalanceToolBar from './CheckbookBalanceToolBar';

const DATA_ITEM_KEY: string = 'rowId';
const initialDataState: State = {
  skip: 0,
  take: 20,
  sort: [
    {
      field: 'date',
      dir: 'desc',
    },
  ],
};
const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};
const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'single',
};
const defaultProps = {
  tableTextId: 'Transactions',
  DataItemKey: DATA_ITEM_KEY,
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  pageable: gridPagerSettings,
} as const;

interface Props {
  glBudget: GLCodesBudgetResponseObject;
}
interface CommittedAmountExcelData extends Omit<ICommittedAmountTransaction, 'total' | 'items'> {
  glCode: string;
  fiscalYear: string | undefined;
  period: string | undefined;
  total: string;
  lineItem?: string;
  itemNum?: string;
  description?: string;
  amount?: string;
  companyName?: string;
}

const CheckbookCommittedAmountTable: FC<Props> = ({ glBudget }) => {
  const { dropDownPeriodValue, t, committedAmountData, setCommittedAmountData, selectedCompany } =
    useViewCheckbook();
  const committedAmountColumns = useHoldAmountColumns(false);
  const committedAmountDetailColumns = useHoldAmountDetailColumns();
  const { getCommittedAmountTransaction, isCommittedLoading } = useCommittedAmountApi();
  const [dataState, setDataState] = useState(initialDataState);
  const [exportLoading, setExportLoading] = useState(false);

  const [selectedView, setSelectedView] = useState(0);
  const handleSelect = useCallback((e: TabStripSelectEventArguments) => {
    const { selected } = e;
    setSelectedView(selected);
    setDataState(initialDataState);
  }, []);

  const { data, total } = useMemo(
    () => process(committedAmountData || [], fixDateSort(dataState)),
    [dataState, committedAmountData]
  );
  const { companyName } = selectedCompany;
  const onDataStateChange = useCallback((e: GridDataStateChangeEvent) => {
    setDataState(e.dataState);
  }, []);

  const handleClearFilter = useCallback(() => {
    setDataState((prev) => ({
      ...initialDataState,
      skip: prev.skip,
      take: prev.take,
    }));
  }, []);

  useEffect(() => {
    if (glBudget?.id && dropDownPeriodValue?.glPeriodId) {
      getCommittedAmountTransaction({
        glCodeId: glBudget?.id,
        glPeriodId: dropDownPeriodValue?.glPeriodId,
      }).then((res) => {
        const data = orderBy(
          res.filter(
            (item) =>
              round(
                sumBy(
                  res.filter((i) => i.poId === item.poId),
                  'total'
                ),
                2
              ) !== 0
          ),
          'actualDate',
          'desc'
        );
        setCommittedAmountData(selectedView === 0 ? data : res);
      });
    }
  }, [
    glBudget?.id,
    dropDownPeriodValue?.glPeriodId,
    getCommittedAmountTransaction,
    setCommittedAmountData,
    selectedView,
  ]);

  const calculateExcelData = useCallback(
    (page: number) => {
      let excelData: CommittedAmountExcelData[] = [];
      const rows = page === 1 ? (data as ICommittedAmountTransaction[]) : committedAmountData;

      rows.forEach((obj) => {
        if (isNull(obj.items) || isEmpty(obj.items)) {
          const newData = {
            ...obj,
            glCode: glBudget?.code,
            fiscalYear: dropDownPeriodValue?.fiscalYearDescription,
            period: dropDownPeriodValue?.content,
            total: formatNumber(obj.total, 'c2'),
            companyName,
          };
          excelData.push(newData);
        } else {
          obj.items.forEach((item: any) => {
            const newData = {
              ...item,
              ...obj,
              glCode: glBudget?.code,
              fiscalYear: dropDownPeriodValue?.fiscalYearDescription,
              period: dropDownPeriodValue?.content,
              total: formatNumber(obj.total, 'c2'),
              amount: formatNumber(item.amount, 'c2'),
              companyName,
            };
            excelData.push(newData);
          });
        }
      });

      return excelData;
    },
    [
      companyName,
      data,
      dropDownPeriodValue?.content,
      dropDownPeriodValue?.fiscalYearDescription,
      glBudget?.code,
      committedAmountData,
    ]
  );

  const excelExport = useCallback(
    (page: number) => {
      const rows = calculateExcelData(page);
      const columns: string[] = [];
      const columnMapping: Record<string, string> = {};
      const excelColumns = [
        { field: 'companyName', title: t('Company Name') },
        { field: 'glCode', title: t('GL Code') },
        { field: 'fiscalYear', title: t('Fiscal Year #') },
        { field: 'period', title: t('Accounting Period #') },
        ...committedAmountColumns.filter((item) => item?.field !== 'expanded'),
        ...committedAmountDetailColumns,
      ];
      excelColumns.forEach((item) => {
        if (item.field === 'date') {
          columns.push('transactionDate');
          Object.assign(columnMapping, { transactionDate: item.title });
        } else {
          columns.push(item.field as string);
          Object.assign(columnMapping, { [item.field as string]: item.title });
        }
      });
      exportToExcel({
        columns,
        rows,
        fileName: t('Committed Amount Transactions'),
        columnMapping,
        sheetName: t('Committed Amount Line Items'),
      });
    },
    [calculateExcelData, committedAmountColumns, committedAmountDetailColumns, t]
  );

  const gridToolbarChildren = useMemo(
    () => (
      <CheckbookBalanceToolBar
        setExportLoading={setExportLoading}
        clearFilter={handleClearFilter}
        excelExport={excelExport}
        dataState={dataState}
      />
    ),
    [dataState, excelExport, handleClearFilter]
  );

  const DetailComponent = useCallback(
    (props: GridDetailRowProps) => (
      <GridWrapper
        dataSource={props.dataItem.items}
        columns={committedAmountDetailColumns}
        DataItemKey="field"
        dataState={dataState}
        className="no_Veritcal_ScrollBar"
      />
    ),
    [dataState, committedAmountDetailColumns]
  );

  return (
    <>
      <div className="pb-2">
        <TabStrip selected={selectedView} onSelect={handleSelect}>
          <TabStripTab contentClassName="!hidden" title={t('Show Current')} />
          <TabStripTab contentClassName="!hidden" title={t('Show All')} />
        </TabStrip>
      </div>

      <GridWrapper
        id="committedAmountTransactionGrid"
        columns={committedAmountColumns}
        dataSource={data}
        dataState={dataState}
        height={'60vh'}
        total={total}
        onDataStateChange={onDataStateChange}
        gridToolbarChildren={gridToolbarChildren}
        detail={DetailComponent}
        loading={isCommittedLoading || exportLoading}
        isCustomExpandableRow
        {...defaultProps}
      />
    </>
  );
};

export default CheckbookCommittedAmountTable;
